import type { ReactNode } from 'react';
import { BarChart3, DollarSign, Eye, MessageSquare, Target } from '../icons';

export interface Feature {
  name: string;
  /** Plain description of the mechanism. */
  does: string;
  /** The business outcome that mechanism produces. */
  benefit: string;
}

export interface FeatureCategory {
  category: string;
  icon: ReactNode;
  features: readonly Feature[];
}

export const FEATURE_CATEGORIES: readonly FeatureCategory[] = [
  {
    category: 'Content Production',
    icon: <MessageSquare size={24} strokeWidth={1.5} />,
    features: [
      {
        name: 'LinkedIn, Every Weekday',
        does: 'Five text and image posts a week, written and queued ahead',
        benefit: 'The channel where your buyers already are, worked daily instead of when you remember.',
      },
      {
        name: 'Instagram and Facebook',
        does: 'Five a week, rotating carousels plus the photos and clips from your day, resized per platform',
        benefit: 'The work you already do every day becomes the feed, without you touching a template.',
      },
      {
        name: 'EGC, Shot On A Phone',
        does: 'Photos and clips of you or your team working, filmed on a phone and directed by me',
        benefit: 'Proof you are real, with no production setup and no days off fulfilment.',
      },
    ],
  },
  {
    category: 'Grounded In Your Business',
    icon: <Target size={24} strokeWidth={1.5} />,
    features: [
      {
        name: 'Objection Miner',
        does: 'Scans your own sales calls and email chains for recurring questions',
        benefit: 'Content answers the objection you actually get, not one I guessed at.',
      },
      {
        name: 'Objection Scriptwriter',
        does: 'Drafts hook, reframe, proof and CTA against the most frequent objections',
        benefit: 'Prospects resolve the hesitation themselves before the call. That sticks harder.',
      },
      {
        name: 'Directive Files',
        does: 'Your voice, offers, pricing, ICP and objections documented up front',
        benefit: 'The system runs the way you would run it. That is what stops it sounding generic.',
      },
      {
        name: 'Human Check On Everything',
        does: 'I read every piece before it publishes, no exceptions',
        benefit: 'Your voice, not raw AI. This is the whole answer to the AI slop problem.',
      },
    ],
  },
  {
    category: 'Lead Response and Reviews',
    icon: <Eye size={24} strokeWidth={1.5} />,
    features: [
      {
        name: 'Speed-to-Lead Agent',
        does: 'Fires on any ad or web form submit, qualifies in conversation, sends a booking link',
        benefit: 'Contact odds fall more than tenfold across the first hour. This closes that window.',
      },
      {
        name: 'Reputation Agent',
        does: 'Fires on service completion, asks for the review on the customer preferred channel',
        benefit: '97% of people read reviews before choosing. Now every job feeds that.',
      },
      {
        name: 'Engaged Leads Database',
        does: 'Logs competitor followers and commenters asking real questions',
        benefit: 'You become the one who answers what they were already asking out loud.',
      },
    ],
  },
  {
    category: 'Meta Ads',
    icon: <DollarSign size={24} strokeWidth={1.5} />,
    features: [
      {
        name: 'Built And Managed On Meta',
        does: 'Campaign build, creative and ongoing management across Facebook and Instagram',
        benefit: 'Paid and organic pull in the same direction instead of being two separate jobs.',
      },
      {
        name: 'Creative From The Content Engine',
        does: 'Ad creative cut from the posts and job footage already proven to land organically',
        benefit: 'You are scaling something with evidence behind it, not guessing at a new hook.',
      },
      {
        name: 'Ads Feed The Same Funnel',
        does: 'Paid traffic lands on surfaces already answering the objections',
        benefit: 'Spend converts better because the content did the convincing before the form.',
      },
    ],
  },
  {
    category: 'The Learning Loop',
    icon: <BarChart3 size={24} strokeWidth={1.5} />,
    features: [
      {
        name: 'Competitor Scan',
        does: 'Scrapes competitor video and posts, reports what is working and what is not',
        benefit: 'You see the gap in your market as data, not as a hunch about what to post.',
      },
      {
        name: 'Content Performance Tracker',
        does: 'Logs weekly performance and attributes wins to their actual cause',
        benefit: 'The system repeats what worked on purpose instead of by luck.',
      },
      {
        name: 'Weekly Report',
        does: 'One plain summary of what ran, what landed, and what changes next',
        benefit: 'You stay across it in minutes, without sitting through a status call.',
      },
      {
        name: 'You Own All Of It',
        does: 'Files, history and reports stay yours',
        benefit: 'If you leave, you leave with the asset. Not a login you lose access to.',
      },
    ],
  },
];
